import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldType } from 'types/FieldTypes';
import { useSettings } from 'services/settings/SettingsContext';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { generateFieldComponents } from 'components/forms/GenerateFieldComponents';
import FormFieldContext from 'components/forms/FormFieldContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { saveData } from 'services/api/saveData';
import { useFetchData } from 'services/api/useFetchData';
import { formatDate } from 'services/utils/dateTimeUtils';
import { deleteData } from 'services/api/deleteData';
import { SubscriptionPackage } from '../settings/billing/BillingTypes';
import { useAllowedRight } from 'services/permissions/permissionChecks';

// The subscription row of the superuser subscription list
export interface ManagedSubscription {
    id: number;
    environment_id: number;
    environment_name: string;
    package: string;
    package_name: string;
    subscription_status: string;
    start_date: string;
    end_date: string;
    duration: string;
    normal_seats: number;
    field_seats: number;
    light_seats: number;
    cancellation_date: string | null;
    pending_subscription: {
        id: number;
        package: string;
        package_name: string;
        duration: string;
        start_date: string;
        end_date: string;
        seats: number;
        users_to_deactivate: number;
    } | null;
    seat_prices: {
        seats?: string | null;
        field_seats?: string | null;
        light_seats?: string | null;
    };
    custom_price: {
        id: number;
        yearly_price: string;
        monthly_price: string | null;
        valid_from: string;
        applied_on: string | null;
        applied_subscription_id: number | null;
    } | null;
}

// An active user of the environment with the seat type it occupies
interface EnvironmentUser {
    id: number;
    full_name: string;
    email: string;
    seat_type: 'seats' | 'field_seats' | 'light_seats';
    is_superuser: boolean;
}

// Adds one day to a date string and returns an iso date string
const addOneDay = (dateString: string): string => {
    const dateObject = new Date(dateString);
    dateObject.setDate(dateObject.getDate() + 1);
    return dateObject.toISOString().slice(0, 10);
};

const SubscriptionManageModal: React.FC<{ viewKey?: string, subscription: ManagedSubscription }> = ({ viewKey, subscription }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { activeFeatures } = useAuthContext();
    const { setFloatingAlert, errorMessages, setErrorMessages } = useGlobalContext();
    const { revealModal, closeModal, modalStack } = useModal();
    const [editing, setEditing] = useState(true);
    const [cancelData, setCancelData] = useState<Record<string, any>>({});
    const [changeData, setChangeData] = useState<Record<string, any>>({});
    const [priceData, setPriceData] = useState<Record<string, any>>({});
    const [priceLoader, setPriceLoader] = useState(false);
    const [removePriceLoader, setRemovePriceLoader] = useState(false);
    const [cancelLoader, setCancelLoader] = useState(false);
    const [revertLoader, setRevertLoader] = useState(false);
    const [changeLoader, setChangeLoader] = useState(false);
    const [removeLoader, setRemoveLoader] = useState(false);
    const topModal = modalStack[modalStack.length - 1];

    // Fetch the active packages including the hardcoded view only package
    const { response: fetchedPackages } = useFetchData({
        apiUrl: 'get_subscription_package_list',
        params: useMemo(() => ({ is_active: true, include_view_only: true }), [])
    });
    const packages: SubscriptionPackage[] = fetchedPackages?.results || [];

    // Fetch the active users of the environment to select the users which stay active after a seat reduction
    const { response: fetchedUsers } = useFetchData({
        apiUrl: `get_environment_users_as_superuser/${subscription.environment_id}`,
        params: useMemo(() => ({}), [])
    });
    const environmentUsers: EnvironmentUser[] = fetchedUsers?.results || [];

    // Determine the selected package to show the supported seat types
    const selectedPackageKey = changeData.package || subscription.package;
    const selectedPackage = packages.find(pkg => pkg.key === selectedPackageKey);
    const hasFieldSeats = selectedPackage?.enable_field_seats ?? false;
    const hasLightSeats = selectedPackage?.enable_light_seats ?? false;

    // Group the active users per seat type, seat types the selected package does not offer fold into full seats
    const usersPerSeatType = useMemo(() => {
        const grouped: Record<'seats' | 'field_seats' | 'light_seats', EnvironmentUser[]> = { seats: [], field_seats: [], light_seats: [] };
        environmentUsers.forEach(user => {
            const seatType = (user.seat_type === 'field_seats' && !hasFieldSeats) || (user.seat_type === 'light_seats' && !hasLightSeats) ? 'seats' : user.seat_type;
            grouped[seatType].push(user);
        });
        return grouped;
    }, [environmentUsers, hasFieldSeats, hasLightSeats]);

    // The seats of the scheduled change per seat type, the current values when nothing is changed
    const enteredSeats = {
        seats: Number(changeData.seats ?? subscription.normal_seats),
        field_seats: hasFieldSeats ? Number(changeData.field_seats ?? subscription.field_seats) : 0,
        light_seats: hasLightSeats ? Number(changeData.light_seats ?? subscription.light_seats) : 0,
    };

    // The seat types with more active users than seats after the change, which need a user selection
    const reducedSeatTypes = (['seats', 'field_seats', 'light_seats'] as const).filter(
        seatType => usersPerSeatType[seatType].length > enteredSeats[seatType]
    );

    // Determine which actions apply to the subscription status
    const isRunning = ['active', 'ending'].includes(subscription.subscription_status);
    const isCancelled = subscription.subscription_status === 'cancelled';

    // Only superusers may set a custom price, staff users never change prices
    const isSuperuser = useAllowedRight('no_user');

    // Reveal the modal on first render
    useEffect(() => {
        revealModal();
    }, []);

    // Set the fields of the cancellation form
    const cancelFields: FieldType[] = [
        {
            name: 'cancellation_reason',
            label: 'billing.cancellation.cancellation_reason_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: true,
            hardcodedOptions: [
                { value: 'too_expensive', name: 'billing.cancellation.reason.too_expensive' },
                { value: 'missing_features', name: 'billing.cancellation.reason.missing_features' },
                { value: 'too_complex', name: 'billing.cancellation.reason.too_complex' },
                { value: 'low_usage', name: 'billing.cancellation.reason.low_usage' },
                { value: 'technical_issues', name: 'billing.cancellation.reason.technical_issues' },
                { value: 'bad_support', name: 'billing.cancellation.reason.bad_support' },
                { value: 'business_closed', name: 'billing.cancellation.reason.business_closed' },
                { value: 'found_alternative', name: 'billing.cancellation.reason.found_alternative' },
                { value: 'other', name: 'billing.cancellation.reason.other' },
            ],
        },
        {
            type: 'textarea',
            name: 'cancellation_remarks',
            label: 'subscription.manage.cancellation_remarks_label',
            placeholder: 'billing.cancellation.cancellation_remark_placeholder',
            rows: 2,
        },
    ];

    // Set the fields of the scheduled change form
    const changeFields: FieldType[] = [
        {
            name: 'package',
            label: 'subscription.general.package_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: packages.map(pkg => ({ value: pkg.key, name: pkg.name })),
        },
        {
            name: 'duration',
            label: 'subscription.general.duration_label',
            type: 'dropdown',
            disabled_selected: 'forms.select_type_disabled_selected',
            showSearch: false,
            allowNoneOption: false,
            hardcodedOptions: [
                { value: 'monthly', name: 'subscription.duration.monthly_long' },
                { value: 'yearly', name: 'subscription.duration.yearly_long' },
            ],
        },
        {
            name: 'seats',
            label: 'subscription.general.full_seats_label',
            type: 'number',
            placeholder: 'subscription.general.full_seats_placeholder',
            min: 1,
        },
        // Only show the field seats when the selected package enables them
        ...(hasFieldSeats ? [{
            name: 'field_seats',
            label: 'subscription.general.field_seats_label',
            type: 'number',
            placeholder: 'subscription.general.field_seats_placeholder',
            min: 0,
        } as FieldType] : []),
        // Only show the light seats when the selected package enables them
        ...(hasLightSeats ? [{
            name: 'light_seats',
            label: 'subscription.general.light_seats_label',
            type: 'number',
            placeholder: 'subscription.general.field_seats_placeholder',
            min: 0,
        } as FieldType] : []),
        // Select the users which stay active for every seat type with fewer seats than active users
        ...reducedSeatTypes.map(seatType => ({
            type: 'multiselect',
            name: `retained_${seatType}`,
            label: `subscription.manage.retained_${seatType}_label`,
            helperText: 'subscription.manage.retained_users_helperText',
            disabled_selected: 'forms.select_type_disabled_selected',
            allowNoneOption: false,
            showSearch: true,
            sortOptions: false,
            hardcodedOptions: usersPerSeatType[seatType].map(user => ({
                value: String(user.id),
                name: user.full_name ? `${user.full_name} (${user.email})` : user.email,
            })),
        } as FieldType)),
    ];

    // Set the fields of the custom price form
    const priceFields: FieldType[] = [
        {
            name: 'yearly_price',
            label: 'subscription.manage.yearly_price_label',
            type: 'text',
            placeholder: '29.00',
            width: '50%',
            group: 'field-row',
        },
        {
            name: 'monthly_price',
            label: 'subscription.manage.monthly_price_label',
            type: 'text',
            placeholder: '36.25',
            helperText: 'subscription.manage.monthly_price_helperText',
            width: '50%',
            group: 'field-row',
        },
        {
            type: 'date',
            name: 'valid_from',
            label: 'subscription.manage.valid_from_label',
        },
    ];

    // The current custom price values which prefill the custom price form, memoized because
    // the text fields reset their value whenever the data object identity changes
    const priceFormData = useMemo(() => ({
        yearly_price: subscription.custom_price?.yearly_price ?? '',
        monthly_price: subscription.custom_price?.monthly_price ?? '',
        valid_from: subscription.custom_price?.valid_from ?? '',
    }), [subscription.custom_price]);

    // The current values which prefill the scheduled change form
    const changeFormData = useMemo(() => ({
        package: subscription.package,
        duration: subscription.duration,
        seats: subscription.normal_seats,
        field_seats: subscription.field_seats,
        light_seats: subscription.light_seats,
    }), [subscription]);

    // Generate the field components of the cancellation form
    const cancelFieldComponents = useMemo(() => {
        if (!viewKey) return;
        return generateFieldComponents(`${viewKey}-cancel`, cancelFields, undefined, undefined, {}, cancelData, activeFeatures);
    }, [cancelData]);

    // Generate the field components of the scheduled change form
    const changeFieldComponents = useMemo(() => {
        if (!viewKey) return;
        return generateFieldComponents(`${viewKey}-change`, changeFields, undefined, undefined, changeFormData, changeData, activeFeatures);
    }, [changeData, packages, hasFieldSeats, hasLightSeats, environmentUsers, reducedSeatTypes.join(',')]);

    // Generate the field components of the custom price form
    const priceFieldComponents = useMemo(() => {
        if (!viewKey) return;
        return generateFieldComponents(`${viewKey}-price`, priceFields, undefined, undefined, priceFormData, priceData, activeFeatures);
    }, [priceData]);

    // Finish an action by alerting, refreshing the list and closing the modal
    const finishAction = (response: any) => {
        setFloatingAlert({ 'type': 'success' });
        if (topModal?.props?.onSuccess) {
            topModal.props.onSuccess(response?.data);
        }
        closeModal();
    };

    // Cancel the subscription without sending emails
    const handleCancel = async () => {
        try {
            setCancelLoader(true);
            const response = await saveData({
                apiUrl: `cancel_subscription_as_superuser/${subscription.id}`,
                method: 'patch',
                data: cancelData
            });
            if (response?.status === 200) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error cancelling subscription', error);
        } finally {
            setCancelLoader(false);
        }
    };

    // Revert the cancellation of the subscription
    const handleRevert = async () => {
        try {
            setRevertLoader(true);
            const response = await saveData({
                apiUrl: `revert_subscription_cancellation_as_superuser/${subscription.id}`,
                method: 'patch',
                data: {}
            });
            if (response?.status === 200) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error reverting cancellation', error);
        } finally {
            setRevertLoader(false);
        }
    };

    // Schedule the change which starts after the current period
    const handleSchedule = async () => {
        try {
            setChangeLoader(true);

            // Collect the users which stay active per reduced seat type, every reduced type needs a selection
            const retainedUserIds: number[] = [];
            for (const seatType of reducedSeatTypes) {
                const selected: string[] = changeData[`retained_${seatType}`] || [];
                if (selected.length === 0) {
                    setErrorMessages({ general: 'subscription.manage.retained_users_required' });
                    return;
                }
                if (selected.length > enteredSeats[seatType]) {
                    setErrorMessages({ general: 'subscription.manage.retained_users_too_many' });
                    return;
                }
                retainedUserIds.push(...selected.map(Number));
            }

            // Combine the current values with the changed values, unsupported seat types become zero
            const data = {
                package: selectedPackageKey,
                duration: changeData.duration ?? subscription.duration,
                seats: changeData.seats ?? subscription.normal_seats,
                field_seats: hasFieldSeats ? (changeData.field_seats ?? subscription.field_seats) : 0,
                light_seats: hasLightSeats ? (changeData.light_seats ?? subscription.light_seats) : 0,
                retained_user_ids: reducedSeatTypes.length > 0 ? retainedUserIds : null,
            };

            const response = await saveData({
                apiUrl: `post_pending_subscription_as_superuser/${subscription.id}`,
                method: 'post',
                data
            });
            if (response?.status === 201) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error scheduling subscription change', error);
        } finally {
            setChangeLoader(false);
        }
    };

    // Save the custom price of the environment for the package of the subscription
    const handleSavePrice = async () => {
        try {
            setPriceLoader(true);

            // Combine the current values with the changed values
            const data = {
                environment: subscription.environment_id,
                package: subscription.package,
                yearly_price: priceData.yearly_price ?? priceFormData.yearly_price,
                monthly_price: priceData.monthly_price ?? priceFormData.monthly_price,
                valid_from: priceData.valid_from ?? priceFormData.valid_from,
            };

            const response = await saveData({
                apiUrl: 'post_environment_package_price_as_superuser',
                method: 'post',
                data
            });
            if (response?.status === 200 || response?.status === 201) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error saving custom price', error);
        } finally {
            setPriceLoader(false);
        }
    };

    // Remove the custom price of the environment
    const handleRemovePrice = async () => {
        if (!subscription.custom_price) return;
        try {
            setRemovePriceLoader(true);
            const response = await deleteData({
                apiUrl: `delete_environment_package_price_as_superuser/${subscription.custom_price.id}`
            });
            if (response?.status === 200) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error removing custom price', error);
        } finally {
            setRemovePriceLoader(false);
        }
    };

    // Remove the scheduled change of the subscription
    const handleRemovePending = async () => {
        try {
            setRemoveLoader(true);
            const response = await deleteData({
                apiUrl: `delete_pending_subscription_as_superuser/${subscription.id}`
            });
            if (response?.status === 200) {
                finishAction(response);
            }
        } catch (error) {
            console.error('Error removing scheduled change', error);
        } finally {
            setRemoveLoader(false);
        }
    };

    // Close the modal
    const handleClose = () => {
        setErrorMessages({});
        closeModal();
    };

    // Render the current seat prices of the subscription lines
    const renderSeatPrices = () => {
        const parts = [];
        if (subscription.seat_prices?.seats) parts.push(t('subscription.manage.seat_price_full', { price: subscription.seat_prices.seats }));
        if (subscription.seat_prices?.field_seats) parts.push(t('subscription.manage.seat_price_field', { price: subscription.seat_prices.field_seats }));
        if (subscription.seat_prices?.light_seats) parts.push(t('subscription.manage.seat_price_light', { price: subscription.seat_prices.light_seats }));
        return parts.join(', ');
    };

    // Render the status of the custom price, applied, waiting for its date or starting at the renewal
    const renderCustomPriceStatus = () => {
        const customPrice = subscription.custom_price;
        if (!customPrice) return null;
        if (customPrice.applied_on) {
            return t('subscription.manage.custom_price_applied_on', { date: formatDate(customPrice.applied_on, userLocale) });
        }
        if (customPrice.valid_from <= subscription.end_date) {
            return t('subscription.manage.custom_price_pending');
        }
        return t('subscription.manage.custom_price_at_renewal');
    };

    // Render the seats summary of the subscription
    const renderSeats = () => {
        const parts = [`${subscription.normal_seats} ${t('subscription.manage.full_seats_short')}`];
        if (subscription.field_seats > 0) parts.push(`${subscription.field_seats} ${t('subscription.manage.field_seats_short')}`);
        if (subscription.light_seats > 0) parts.push(`${subscription.light_seats} ${t('subscription.manage.light_seats_short')}`);
        return parts.join(', ');
    };

    return (
        <div className='subscription-manage-modal'>
            {(errorMessages.general) &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }

            {/* Summary of the current subscription */}
            <div className='subscription-manage-summary'>
                <h5>{subscription.environment_name}</h5>
                <p>{subscription.package_name} · {t(`subscription.duration.${subscription.duration}_long`)} · {renderSeats()}</p>
                <p>{formatDate(subscription.start_date, userLocale)} - {formatDate(subscription.end_date, userLocale)} · {t(`subscription.status.${subscription.subscription_status}`)}</p>
                {subscription.cancellation_date && (
                    <p>{t('subscription.manage.cancelled_on_label', { date: formatDate(subscription.cancellation_date, userLocale) })}</p>
                )}
            </div>

            {/* Scheduled change of the subscription */}
            {subscription.pending_subscription && (
                <div className='subscription-manage-section'>
                    <h6>{t('subscription.manage.pending_change_header')}</h6>
                    <p>
                        {t('subscription.manage.pending_change_summary', {
                            packageName: subscription.pending_subscription.package_name,
                            duration: t(`subscription.duration.${subscription.pending_subscription.duration}_long`).toLowerCase(),
                            seats: subscription.pending_subscription.seats,
                            date: formatDate(subscription.pending_subscription.start_date, userLocale),
                        })}
                        {subscription.pending_subscription.users_to_deactivate > 0 && (
                            <> {t('subscription.manage.pending_change_deactivations', { count: subscription.pending_subscription.users_to_deactivate })}</>
                        )}
                    </p>
                    <div className='buttons-right'>
                        <SecondaryButton
                            onClick={handleRemovePending}
                            label='subscription.manage.remove_pending_change_button_label'
                            size='small'
                            loading={removeLoader}
                        />
                    </div>
                </div>
            )}

            {/* Custom price of the environment for the package, only for superusers */}
            {isRunning && isSuperuser && (
                <FormFieldContext.Provider value={{ editing, setEditing, updatedData: priceData, setUpdatedData: setPriceData }}>
                    <div className='subscription-manage-section'>
                        <h6>{t('subscription.manage.price_header')}</h6>
                        <p className='helper-text'>{t('subscription.manage.price_explanation')}</p>
                        {renderSeatPrices() && (
                            <p className='helper-text'>{t('subscription.manage.current_seat_prices_label', { prices: renderSeatPrices() })}</p>
                        )}
                        {subscription.custom_price && (
                            <p>
                                {t('subscription.manage.custom_price_summary', {
                                    yearlyPrice: subscription.custom_price.yearly_price,
                                    monthlyPrice: subscription.custom_price.monthly_price ? `€ ${subscription.custom_price.monthly_price}` : t('subscription.manage.custom_price_monthly_derived'),
                                    date: formatDate(subscription.custom_price.valid_from, userLocale),
                                })} {renderCustomPriceStatus()}
                            </p>
                        )}
                        <form className='formset add-edit-form'>
                            {priceFieldComponents}
                            <div className='buttons-right'>
                                {subscription.custom_price && (
                                    <SecondaryButton
                                        onClick={handleRemovePrice}
                                        label='subscription.manage.remove_price_button_label'
                                        size='small'
                                        loading={removePriceLoader}
                                    />
                                )}
                                <PrimaryButton
                                    type='button'
                                    label='subscription.manage.save_price_button_label'
                                    onClick={handleSavePrice}
                                    size='small'
                                    loading={priceLoader}
                                />
                            </div>
                        </form>
                    </div>
                </FormFieldContext.Provider>
            )}

            {/* Schedule a change which starts after the current period */}
            {isRunning && (
                <FormFieldContext.Provider value={{ editing, setEditing, updatedData: changeData, setUpdatedData: setChangeData }}>
                    <div className='subscription-manage-section'>
                        <h6>{t('subscription.manage.schedule_change_header', { date: formatDate(addOneDay(subscription.end_date), userLocale) })}</h6>
                        <p className='helper-text'>{t('subscription.manage.schedule_change_explanation')}</p>
                        <form className='formset add-edit-form'>
                            {changeFieldComponents}
                            <div className='buttons-right'>
                                <PrimaryButton
                                    type='button'
                                    label='subscription.manage.schedule_change_button_label'
                                    onClick={handleSchedule}
                                    size='small'
                                    loading={changeLoader}
                                />
                            </div>
                        </form>
                    </div>
                </FormFieldContext.Provider>
            )}

            {/* Cancel the subscription without sending emails */}
            {isRunning && (
                <FormFieldContext.Provider value={{ editing, setEditing, updatedData: cancelData, setUpdatedData: setCancelData }}>
                    <div className='subscription-manage-section'>
                        <h6>{t('subscription.manage.cancel_header')}</h6>
                        <p className='helper-text'>{t('subscription.manage.cancel_explanation', { date: formatDate(subscription.end_date, userLocale) })}</p>
                        <form className='formset add-edit-form'>
                            {cancelFieldComponents}
                            <div className='buttons-right'>
                                <PrimaryButton
                                    type='button'
                                    label='subscription.manage.cancel_button_label'
                                    onClick={handleCancel}
                                    size='small'
                                    loading={cancelLoader}
                                    customClass='danger'
                                />
                            </div>
                        </form>
                    </div>
                </FormFieldContext.Provider>
            )}

            {/* Revert the cancellation of a running subscription */}
            {isCancelled && (
                <div className='subscription-manage-section'>
                    <h6>{t('subscription.manage.revert_header')}</h6>
                    <p className='helper-text'>{t('subscription.manage.revert_explanation')}</p>
                    <div className='buttons-right'>
                        <SecondaryButton
                            onClick={handleRevert}
                            label='subscription.manage.revert_button_label'
                            size='small'
                            loading={revertLoader}
                        />
                    </div>
                </div>
            )}

            <div className='buttons-right'>
                <SecondaryButton
                    onClick={handleClose}
                    label='general.close'
                    size='small'
                />
            </div>
        </div>
    );
};

export default SubscriptionManageModal;
